/**
 * Branding settings (Settings → Branding). Owner-only. Edits the signed-in
 * org's brand name, logo, accent color, links, and the "Powered by
 * RunMyTrivia" mark on the projector.
 *
 * Values come from the org-scoped SiteConfigProvider that wraps the
 * operator shell. Each save calls reload(), so the shell accent and name
 * update at once. Logo, color, and the mark toggle need the
 * customBranding plan feature. The server enforces this too.
 */
import { useState } from 'react';
import { Box, Callout, Card, Flex, Heading, Switch, Text } from '@radix-ui/themes';
import api from '../../utils/api';
import { BRANDING_FIELDS, useSiteConfig } from '../../utils/siteConfig';
import { SiteConfigRow } from './SiteContentManager';

const PoweredByToggle: React.FC<{ enabled: boolean; initialHidden: boolean; onSaved: () => void }> = ({
  enabled,
  initialHidden,
  onSaved,
}) => {
  const [hidden, setHidden] = useState(initialHidden);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const change = async (next: boolean) => {
    setSaving(true);
    setError(null);
    try {
      if (next) {
        await api.put('/api/site-config/hidePoweredBy', { value: 'true' });
      } else {
        await api.delete('/api/site-config/hidePoweredBy');
      }
      setHidden(next);
      onSaved();
    } catch (err) {
      const e = err as { response?: { data?: { message?: string } }; message?: string };
      setError(e.response?.data?.message || e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card size="2">
      <Flex justify="between" align="center" gap="3">
        <Box>
          <Text as="div" weight="medium">Hide “Powered by RunMyTrivia”</Text>
          <Text as="div" size="1" color="gray">
            The projector shows a small RunMyTrivia mark in the corner. Turn this on to hide it.
          </Text>
        </Box>
        <Switch
          checked={hidden}
          disabled={!enabled || saving}
          onCheckedChange={(v) => change(v)}
          aria-label="Hide Powered by RunMyTrivia"
        />
      </Flex>
      {error && <Text as="div" size="1" color="red" mt="2">{error}</Text>}
    </Card>
  );
};

const BrandingSettings: React.FC = () => {
  const { config, meta, loaded, reload } = useSiteConfig();
  const customBranding = meta?.customBranding ?? false;

  if (!loaded) {
    return <Box p="4"><Text color="gray">Loading…</Text></Box>;
  }

  return (
    <Box p="4">
      <Heading size="5" mb="2">Branding</Heading>
      <Text as="p" color="gray" size="2" mb="4" style={{ maxWidth: 720 }}>
        Your brand shows on the projector, your public site, and this app.
        Each row saves independently.
      </Text>
      {!customBranding && (
        <Callout.Root color="amber" mb="4" style={{ maxWidth: 780 }}>
          <Callout.Text>
            A custom logo, a custom color, and hiding the RunMyTrivia mark need a
            plan that includes custom branding.
          </Callout.Text>
        </Callout.Root>
      )}
      <Flex direction="column" gap="3" maxWidth="780px">
        {BRANDING_FIELDS.map((field) => (
          <SiteConfigRow
            key={field.key}
            field={field}
            initialValue={config[field.key] ?? ''}
            disabled={Boolean(field.gated) && !customBranding}
            onSaved={reload}
          />
        ))}
        <PoweredByToggle
          enabled={customBranding}
          initialHidden={config.hidePoweredBy === 'true'}
          onSaved={reload}
        />
      </Flex>
    </Box>
  );
};

export default BrandingSettings;
